"use client";

import { useEffect, useRef } from "react";
import { createClient } from "@/lib/supabase-browser";
import { api } from "@/lib/api-client";

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new ArrayBuffer(raw.length);
  const view = new Uint8Array(arr);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Invisible component that registers the service worker and subscribes
 * the authenticated user to Web Push notifications.
 * Renders nothing — mount once in the nav bar or layout.
 */
export function PushManager() {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!VAPID_PUBLIC_KEY) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    void (async () => {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Register service worker
        const registration = await navigator.serviceWorker.register("/sw.js");

        // Check existing subscription
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          // Only subscribe if the user has already granted permission
          // (we don't want to prompt unprompted — the bell component handles that)
          if (Notification.permission !== "granted") return;

          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
        }

        // Send subscription to backend
        const json = subscription.toJSON();
        await api.post("/api/push/subscribe", {
          endpoint: json.endpoint,
          keys: json.keys,
        });
      } catch {
        // Push setup failed silently — not critical
      }
    })();
  }, []);

  return null;
}

/**
 * Request notification permission and subscribe to push.
 * Call this from a user-initiated action (e.g. clicking "Enable notifications").
 * Returns true if subscription succeeded.
 */
export async function requestPushPermission(): Promise<boolean> {
  if (!VAPID_PUBLIC_KEY) return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;

    // Register SW ourselves so we don't depend on PushManager component timing
    const registration = await navigator.serviceWorker.register("/sw.js");
    // Wait for the SW to be active before subscribing
    if (registration.installing || registration.waiting) {
      await new Promise<void>((resolve) => {
        const sw = registration.installing || registration.waiting;
        sw!.addEventListener("statechange", function handler() {
          if (sw!.state === "activated") { sw!.removeEventListener("statechange", handler); resolve(); }
        });
        // Resolve immediately if already activated
        if (sw!.state === "activated") resolve();
      });
    }
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const json = subscription.toJSON();
    const res = await api.post("/api/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
    });

    return res.ok;
  } catch {
    return false;
  }
}
